import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { applyThemeColorsToCssVariables, darkTheme, lightTheme, Theme, ThemeMode } from '../theme';

type ThemePreference = ThemeMode | 'system';

interface ThemeContextProps {
  theme: Theme;
  themePreference: ThemePreference;
  toggleColorMode: () => void;
  setTheme: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);


const getSystemTheme = (): ThemeMode => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light'; 
};

// Helper to resolve theme preference to actual theme
const resolveTheme = (preference: ThemePreference): Theme => {
  if (preference === 'system') {
    const systemMode = getSystemTheme();
    return systemMode === 'dark' ? darkTheme : lightTheme;
  }
  return preference === 'dark' ? darkTheme : lightTheme;
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [themePreference, setThemePreference] = useState<ThemePreference>('system');
  const [theme, setThemeState] = useState<Theme>(() => resolveTheme('system'));


  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (themePreference === 'system') {
        const newTheme = e.matches ? darkTheme : lightTheme;
        setThemeState(newTheme);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [themePreference]);

  
  useEffect(() => {
    const root = window.document.documentElement;
    
    root.classList.remove('light', 'dark');
    
    root.classList.add(theme.mode);
    

    applyThemeColorsToCssVariables(theme.colors);

    localStorage.setItem('theme-preference', themePreference);
  }, [theme, themePreference]);

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    const savedPreference = localStorage.getItem('theme-preference') as ThemePreference | null;
    const initialPreference: ThemePreference = savedPreference || 'system';
    
    setThemePreference(initialPreference);
    const resolvedTheme = resolveTheme(initialPreference);
    setThemeState(resolvedTheme);
    
    // Apply initial theme immediately to prevent flash
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme.mode);
    applyThemeColorsToCssVariables(resolvedTheme.colors);
  }, []);

  const toggleColorMode = () => {
    const currentMode = theme.mode;
    const nextPreference: ThemePreference = currentMode === 'dark' ? 'light' : 'dark';
    setThemePreference(nextPreference);
    setThemeState(resolveTheme(nextPreference));
  };

  const setTheme = (preference: ThemePreference) => {
    setThemePreference(preference);
    setThemeState(resolveTheme(preference));
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      themePreference, 
      toggleColorMode, 
      setTheme 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};